import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthToken, setAuthToken } from '../App';

// Helper function to get proper image URL
const getImageUrl = (imagePath, apiBase) => {
  if (!imagePath || imagePath === '' || imagePath === 'null' || imagePath === 'undefined') return null;
  if (imagePath.startsWith('data:') || imagePath.startsWith('http')) return imagePath;
  // Already has /api prefix
  if (imagePath.startsWith('/api/')) return `${apiBase.replace('/api', '')}${imagePath}`;
  // Old format without /api - add it
  if (imagePath.startsWith('/uploads')) return `${apiBase.replace('/api', '')}/api${imagePath}`;
  return `${apiBase}/${imagePath}`;
};

import { 
  Briefcase, Users, Plus, LogOut, Building2, Mail, Phone, 
  MapPin, Clock, Eye, Edit2, Trash2, CheckCircle, XCircle,
  FileText, TrendingUp, Calendar, Search, Filter, MoreVertical,
  Copy, Globe, Linkedin, Twitter, Upload, Image, Save, 
  BarChart3, PieChart, Target, UserCheck, Download, Send,
  Video, PhoneCall, MapPinned, ChevronRight, Star, Zap,
  Building, Home, Laptop, GraduationCap, Award, DollarSign,
  X, Check, AlertCircle, RefreshCw, ExternalLink, Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format } from 'date-fns';

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [isCompanyProfileDialogOpen, setIsCompanyProfileDialogOpen] = useState(false);
  const [isInterviewDialogOpen, setIsInterviewDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isApplicationDetailOpen, setIsApplicationDetailOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Analytics data
  const [analytics, setAnalytics] = useState(null);
  const [jobAnalytics, setJobAnalytics] = useState([]);
  const [candidateDemographics, setCandidateDemographics] = useState(null);
  
  // Company Profile
  const [companyProfile, setCompanyProfile] = useState({
    logo_url: null,
    banner_url: null,
    about: '',
    mission: '',
    culture: '',
    industry: '',
    company_size: '',
    founded_year: '',
    headquarters: '',
    website: '',
    linkedin: '',
    twitter: '',
    benefits: [],
    perks: [],
    tech_stack: []
  });
  
  // Recommended candidates
  const [recommendedCandidates, setRecommendedCandidates] = useState([]);
  const [selectedJobForCandidates, setSelectedJobForCandidates] = useState(null);
  
  // Job form state
  const [jobForm, setJobForm] = useState({
    position_name: '',
    company_name: '',
    location: '',
    work_type: 'Office',
    employment_type: 'Full-time',
    salary_range: '',
    salary_currency: '₹',
    salary_min: '',
    salary_max: '',
    description: '',
    requirements: '',
    responsibilities: [],
    qualifications: [],
    nice_to_have: [],
    benefits: [],
    required_skills: [],
    experience_required: '',
    education_required: '',
    application_deadline: '',
    is_published: false,
    is_urgent: false,
    is_featured: false
  });
  
  // Interview form
  const [interviewForm, setInterviewForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_type: 'Video Call',
    meeting_link: '',
    location: '',
    duration_minutes: 60,
    notes: ''
  });
  
  // Skill/Benefit input helpers
  const [newSkill, setNewSkill] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  const [newQualification, setNewQualification] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newPerk, setNewPerk] = useState('');
  
  // Subscription & Credits
  const [subscription, setSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  
  // Team Management
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [isInviteTeamDialogOpen, setIsInviteTeamDialogOpen] = useState(false);
  const [teamInviteForm, setTeamInviteForm] = useState({ email: '', name: '', role: 'recruiter' });
  
  // Interview Settings
  const [interviewSettings, setInterviewSettings] = useState({
    calendly_url: '',
    google_meet_enabled: false,
    default_meeting_duration: 30,
    interview_buffer_minutes: 15,
    working_hours_start: '09:00',
    working_hours_end: '18:00',
    working_days: [1, 2, 3, 4, 5]
  });
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    } else if (activeTab === 'subscription') {
      fetchSubscription();
    } else if (activeTab === 'team') {
      fetchTeam();
    } else if (activeTab === 'settings') {
      fetchInterviewSettings();
    }
  }, [activeTab]);

  const fetchUserData = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const userRes = await axios.get(`${API}/auth/me`, config);
      
      if (userRes.data.role !== 'employer') {
        navigate('/dashboard');
        return;
      }
      
      if (userRes.data.approval_status !== 'approved') {
        toast.error('Your employer account is pending approval');
        setAuthToken(null);
        navigate('/login');
        return;
      }
      
      setCurrentUser(userRes.data);
      
      // Fetch data in parallel
      const [jobsRes, appsRes, profileRes] = await Promise.all([
        axios.get(`${API}/employer/jobs`, config),
        axios.get(`${API}/employer/applications`, config),
        axios.get(`${API}/employer/company-profile`, config).catch(() => ({ data: {} }))
      ]);
      
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
      if (profileRes.data) {
        setCompanyProfile(prev => ({ ...prev, ...profileRes.data }));
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        setAuthToken(null);
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [overviewRes, jobsRes, demographicsRes] = await Promise.all([
        axios.get(`${API}/employer/analytics/overview`, config),
        axios.get(`${API}/employer/analytics/jobs`, config),
        axios.get(`${API}/employer/analytics/candidates`, config)
      ]);
      
      setAnalytics(overviewRes.data);
      setJobAnalytics(jobsRes.data);
      setCandidateDemographics(demographicsRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchRecommendedCandidates = async (jobId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/employer/jobs/${jobId}/recommended-candidates`, config);
      setRecommendedCandidates(res.data);
      setSelectedJobForCandidates(jobs.find(j => j.id === jobId));
    } catch (error) {
      toast.error('Failed to fetch recommendations');
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [subRes, plansRes] = await Promise.all([
        axios.get(`${API}/employer/subscription`, config),
        axios.get(`${API}/employer/subscription/plans`, config)
      ]);
      setSubscription(subRes.data);
      setSubscriptionPlans(plansRes.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchTeam = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [teamRes, rolesRes] = await Promise.all([
        axios.get(`${API}/employer/team`, config),
        axios.get(`${API}/employer/team/roles`, config)
      ]);
      setTeamMembers(teamRes.data);
      setTeamRoles(rolesRes.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const fetchInterviewSettings = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/employer/settings/interview`, config);
      setInterviewSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch interview settings:', error);
    }
  };

  const handleUpgradeSubscription = async (planId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/employer/subscription/upgrade`, { plan_id: planId }, config);
      toast.success('Subscription upgraded successfully!');
      setIsUpgradeDialogOpen(false);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade subscription');
    }
  };

  const handleInviteTeamMember = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(`${API}/employer/team/invite`, teamInviteForm, config);
      
      // Show invite link for manual sharing
      if (response.data.invite_link) {
        toast.success(
          <div>
            <p className="font-medium">Team member invited!</p>
            <p className="text-xs mt-1 opacity-80">
              {response.data.email_sent 
                ? 'Email sent successfully' 
                : 'Copy the link below to share manually:'}
            </p>
            {!response.data.email_sent && (
              <div className="mt-2 p-2 bg-white/20 rounded text-xs break-all">
                {response.data.invite_link}
              </div>
            )}
          </div>,
          { duration: response.data.email_sent ? 3000 : 10000 }
        );
        
        // Also copy to clipboard if email wasn't sent
        if (!response.data.email_sent && navigator.clipboard) {
          navigator.clipboard.writeText(response.data.invite_link);
          toast.info('Invite link copied to clipboard!', { duration: 2000 });
        }
      } else {
        toast.success('Team member invited successfully!');
      }
      
      setIsInviteTeamDialogOpen(false);
      setTeamInviteForm({ email: '', name: '', role: 'recruiter' });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite team member');
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/employer/team/${memberId}`, config);
      toast.success('Team member removed');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleUpdateTeamMemberRole = async (memberId, newRole) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/employer/team/${memberId}`, { role: newRole }, config);
      toast.success('Team member role updated');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleSaveInterviewSettings = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/employer/settings/interview`, interviewSettings, config);
      toast.success('Interview settings saved!');
      setIsSettingsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const handleSendSchedulingLink = async (applicationId) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/employer/send-scheduling-link?application_id=${applicationId}`, {}, config);
      toast.success('Scheduling link sent to candidate!');
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send scheduling link');
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    navigate('/login');
  };

  const resetJobForm = () => {
    setJobForm({
      position_name: '',
      company_name: '',
      location: '',
      work_type: 'Office',
      employment_type: 'Full-time',
      salary_range: '',
      salary_currency: '₹',
      salary_min: '',
      salary_max: '',
      description: '',
      requirements: '',
      responsibilities: [],
      qualifications: [],
      nice_to_have: [],
      benefits: [],
      required_skills: [],
      experience_required: '',
      education_required: '',
      application_deadline: '',
      is_published: false,
      is_urgent: false,
      is_featured: false
    });
  };

  const handleCreateJob = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const jobData = {
        ...jobForm,
        company_name: currentUser.company_name || jobForm.company_name
      };
      
      if (editingJob) {
        await axios.put(`${API}/employer/jobs/${editingJob.id}`, jobData, config);
        toast.success('Job updated successfully!');
      } else {
        await axios.post(`${API}/employer/jobs`, jobData, config);
        toast.success('Job created successfully!');
      }
      
      setIsJobDialogOpen(false);
      setEditingJob(null);
      resetJobForm();
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      position_name: job.position_name || '',
      company_name: job.company_name || '',
      location: job.location || '',
      work_type: job.work_type || 'Office',
      employment_type: job.employment_type || 'Full-time',
      salary_range: job.salary_range || '',
      salary_currency: job.salary_currency || '₹',
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      description: job.description || job.job_description || '',
      requirements: job.requirements || '',
      responsibilities: job.responsibilities || [],
      qualifications: job.qualifications || [],
      nice_to_have: job.nice_to_have || [],
      benefits: job.benefits || [],
      required_skills: job.required_skills || [],
      experience_required: job.experience || '',
      education_required: job.education || '',
      application_deadline: job.application_deadline || '',
      is_published: job.is_published || false,
      is_urgent: job.is_urgent || false,
      is_featured: job.is_featured || false
    });
    setIsJobDialogOpen(true);
  };

  const handleDuplicateJob = async (job) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/employer/jobs/${job.id}/duplicate`, {}, config);
      toast.success('Job duplicated! Edit the copy as needed.');
      fetchUserData();
    } catch (error) {
      toast.error('Failed to duplicate job');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/employer/jobs/${jobId}`, config);
      toast.success('Job deleted');
      fetchUserData();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const handleTogglePublish = async (job) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/employer/jobs/${job.id}/publish`, {
        is_published: !job.is_published
      }, config);
      toast.success(job.is_published ? 'Job unpublished' : 'Job published!');
      fetchUserData();
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, status) => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(`${API}/employer/applications/${applicationId}/status`, { status }, config);
      toast.success('Application status updated');
      fetchUserData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveCompanyProfile = async () => {
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.put(`${API}/employer/company-profile`, companyProfile, config);
      toast.success('Company profile updated!');
      setIsCompanyProfileDialogOpen(false);
      fetchUserData();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/employer/company-profile/upload-logo`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setCompanyProfile(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const handleUploadBanner = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = getAuthToken();
      const res = await axios.post(`${API}/employer/company-profile/upload-banner`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setCompanyProfile(prev => ({ ...prev, banner_url: res.data.banner_url }));
      toast.success('Banner uploaded!');
    } catch (error) {
      toast.error('Failed to upload banner');
    }
  };

  const handleSendInterviewInvite = async () => {
    if (!selectedApplication) return;
    
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/employer/send-interview-invite`, {
        application_id: selectedApplication.id,
        ...interviewForm
      }, config);
      
      toast.success('Interview invite sent!');
      setIsInterviewDialogOpen(false);
      setSelectedApplication(null);
      setInterviewForm({
        interview_date: '',
        interview_time: '',
        interview_type: 'Video Call',
        meeting_link: '',
        location: '',
        duration_minutes: 60,
        notes: ''
      });
      fetchUserData();
    } catch (error) {
      toast.error('Failed to send invite');
    }
  };

  const addListItem = (field, value, setValueFn) => {
    if (value.trim()) {
      setJobForm(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
      setValueFn('');
    }
  };

  const removeListItem = (field, index) => {
    setJobForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const filteredJobs = jobs.filter(job => 
    job.position_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.job_title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    totalJobs: jobs.length,
    publishedJobs: jobs.filter(j => j.is_published).length,
    totalApplications: applications.length,
    pendingApplications: applications.filter(a => a.status === 'Applied').length,
    shortlistedApplications: applications.filter(a => a.status === 'Shortlisted').length,
    totalViews: jobs.reduce((sum, j) => sum + (j.views_count || 0), 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#0F4C81] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0F4C81] to-[#10B981] text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="md:hidden p-2 hover:bg-white/10 rounded-lg">
              <Menu className="h-5 w-5" />
            </button>
            {companyProfile.logo_url ? (
              <img src={getImageUrl(companyProfile.logo_url, API)} alt="Logo" className="h-10 w-10 rounded-lg shadow-lg object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center font-bold text-lg">
                {currentUser?.company_name?.charAt(0) || 'E'}
              </div>
            )}
            <div>
              <h1 className="font-bold text-xl">{currentUser?.company_name || 'Employer Dashboard'}</h1>
              <p className="text-xs text-white/70">Manage your recruitment</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsCompanyProfileDialogOpen(true)}
              className="text-white hover:bg-white/20 hidden md:flex"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Company Profile
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-white/20"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalJobs}</p>
                  <p className="text-xs text-gray-500">Total Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.publishedJobs}</p>
                  <p className="text-xs text-gray-500">Published</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalApplications}</p>
                  <p className="text-xs text-gray-500">Applications</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.pendingApplications}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.shortlistedApplications}</p>
                  <p className="text-xs text-gray-500">Shortlisted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-pink-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.totalViews}</p>
                  <p className="text-xs text-gray-500">Job Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <TabsList className="bg-white shadow-md p-1 h-auto flex-wrap">
              <TabsTrigger value="jobs" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <Briefcase className="h-4 w-4 mr-2" />
                Jobs
              </TabsTrigger>
              <TabsTrigger value="applications" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <FileText className="h-4 w-4 mr-2" />
                Applications
              </TabsTrigger>
              <TabsTrigger value="candidates" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <Target className="h-4 w-4 mr-2" />
                Find Talent
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="subscription" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <Zap className="h-4 w-4 mr-2" />
                Plans
              </TabsTrigger>
              <TabsTrigger value="team" className="data-[state=active]:bg-[#0F4C81] data-[state=active]:text-white">
                <Users className="h-4 w-4 mr-2" />
                Team
              </TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  placeholder="Search..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {activeTab === 'jobs' && (
                <Button onClick={() => { resetJobForm(); setEditingJob(null); setIsJobDialogOpen(true); }} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Post Job
                </Button>
              )}
            </div>
          </div>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="mt-0">
            {filteredJobs.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Briefcase className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Jobs Posted Yet</h3>
                  <p className="text-gray-500 mb-4">Start by creating your first job posting</p>
                  <Button onClick={() => { resetJobForm(); setEditingJob(null); setIsJobDialogOpen(true); }} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Job
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredJobs.map((job) => (
                  <Card key={job.id} className="border-0 shadow-md hover:shadow-lg transition-all" data-testid={`job-card-${job.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800 line-clamp-1">{job.position_name}</h3>
                          <p className="text-sm text-gray-500">{job.company_name}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {job.is_urgent && <Badge className="bg-red-100 text-red-700 text-xs">Urgent</Badge>}
                          {job.is_featured && <Badge className="bg-amber-100 text-amber-700 text-xs">Featured</Badge>}
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {job.location && (
                          <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            <MapPin className="h-3 w-3" />
                            {job.location}
                          </span>
                        )}
                        {job.work_type && (
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                            {job.work_type === 'Work from Home' ? <Home className="h-3 w-3" /> : 
                             job.work_type === 'Hybrid' ? <Laptop className="h-3 w-3" /> :
                             <Building className="h-3 w-3" />}
                            {job.work_type === 'Work from Home' ? 'Remote' : job.work_type}
                          </span>
                        )}
                        {job.salary_range && (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-600 px-2 py-1 rounded">
                            {job.salary_currency || '₹'} {job.salary_range}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {job.views_count || 0} views
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {job.applications_count || applications.filter(a => a.job_id === job.id).length} applicants
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t">
                        <Badge className={job.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                          {job.is_published ? 'Published' : 'Draft'}
                        </Badge>
                        
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(job)} title={job.is_published ? 'Unpublish' : 'Publish'}>
                            {job.is_published ? <XCircle className="h-4 w-4 text-gray-500" /> : <CheckCircle className="h-4 w-4 text-green-500" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditJob(job)} title="Edit">
                            <Edit2 className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicateJob(job)} title="Duplicate">
                            <Copy className="h-4 w-4 text-purple-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => fetchRecommendedCandidates(job.id)} title="Find Candidates">
                            <Target className="h-4 w-4 text-emerald-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteJob(job.id)} title="Delete">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="mt-0">
            <div className="flex gap-2 mb-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Applied">New</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Shortlisted">Shortlisted</SelectItem>
                  <SelectItem value="Interview Scheduled">Interview</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {filteredApplications.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Yet</h3>
                  <p className="text-gray-500">Applications will appear here when candidates apply</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredApplications.map((app) => (
                  <Card key={app.id} className="border-0 shadow-md hover:shadow-lg transition-all" data-testid={`application-card-${app.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#10B981] flex items-center justify-center text-white font-bold">
                            {app.full_name?.charAt(0) || 'C'}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-800">{app.full_name}</h4>
                            <p className="text-sm text-gray-500">{app.email}</p>
                            <p className="text-xs text-gray-400 mt-1">Applied for: {app.job_title}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2">
                          {app.total_experience && (
                            <Badge variant="outline" className="text-xs">{app.total_experience} exp</Badge>
                          )}
                          {app.education && (
                            <Badge variant="outline" className="text-xs">{app.education}</Badge>
                          )}
                          <Badge className={
                            app.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                            app.status === 'Under Review' ? 'bg-amber-100 text-amber-700' :
                            app.status === 'Shortlisted' ? 'bg-purple-100 text-purple-700' :
                            app.status === 'Interview Scheduled' ? 'bg-sky-100 text-sky-700' :
                            app.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }>{app.status}</Badge>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" onClick={() => { setSelectedApplication(app); setIsApplicationDetailOpen(true); }}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleUpdateApplicationStatus(app.id, 'Shortlisted')} title="Shortlist">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setSelectedApplication(app); setIsInterviewDialogOpen(true); }} title="Schedule Interview">
                            <Calendar className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleSendSchedulingLink(app.id)} title="Send Self-Schedule Link">
                            <ExternalLink className="h-4 w-4 text-purple-500" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleUpdateApplicationStatus(app.id, 'Rejected')} title="Reject">
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Find Talent Tab */}
          <TabsContent value="candidates" className="mt-0">
            <Card className="border-0 shadow-lg mb-6">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-slate-800 mb-4">Find Matching Candidates</h3>
                <p className="text-gray-600 mb-4">Select a job to find candidates that match the required skills.</p>
                <div className="grid gap-2 md:grid-cols-3">
                  {jobs.filter(j => j.is_published).map(job => (
                    <Button
                      key={job.id}
                      variant={selectedJobForCandidates?.id === job.id ? 'default' : 'outline'}
                      className={selectedJobForCandidates?.id === job.id ? 'bg-[#0F4C81]' : ''}
                      onClick={() => fetchRecommendedCandidates(job.id)}
                    >
                      <Target className="h-4 w-4 mr-2" />
                      {job.position_name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {selectedJobForCandidates && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">
                  Recommended Candidates for &quot;{selectedJobForCandidates.position_name}&quot;
                </h4>
                
                {recommendedCandidates.length === 0 ? (
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-8 text-center">
                      <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-500">No matching candidates found. Try adding more skills to your job posting.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {recommendedCandidates.map(candidate => (
                      <Card key={candidate.id} className="border-0 shadow-md">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                              {candidate.name?.charAt(0) || 'C'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-slate-800">{candidate.name}</h4>
                                <Badge className={
                                  candidate.match_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                                  candidate.match_score >= 40 ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }>
                                  {candidate.match_score}% Match
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-500">{candidate.current_position} {candidate.current_company ? `at ${candidate.current_company}` : ''}</p>
                              <p className="text-xs text-gray-400">{candidate.experience_years} experience</p>
                              
                              <div className="mt-2 flex flex-wrap gap-1">
                                {candidate.matched_skills?.slice(0, 5).map((skill, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs bg-emerald-50 text-emerald-600">
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                              
                              <div className="mt-3 flex gap-2">
                                <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                                  <Mail className="h-3 w-3 mr-1" />
                                  Contact
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-0">
            {analytics ? (
              <div className="space-y-6">
                {/* Overview Cards */}
                <div className="grid gap-4 md:grid-cols-4">
                  <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Total Views</p>
                          <p className="text-3xl font-bold">{analytics.total_views}</p>
                        </div>
                        <Eye className="h-10 w-10 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 shadow-md bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Total Applications</p>
                          <p className="text-3xl font-bold">{analytics.total_applications}</p>
                        </div>
                        <FileText className="h-10 w-10 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Application Rate</p>
                          <p className="text-3xl font-bold">{analytics.application_rate}%</p>
                        </div>
                        <TrendingUp className="h-10 w-10 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm opacity-80">Last 7 Days</p>
                          <p className="text-3xl font-bold">{analytics.recent_applications}</p>
                        </div>
                        <Calendar className="h-10 w-10 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Per-Job Analytics */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-[#0F4C81]" />
                      Job Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {jobAnalytics.map(job => (
                        <div key={job.job_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <h5 className="font-medium text-slate-800">{job.position_name}</h5>
                            <div className="flex gap-4 text-sm text-gray-500 mt-1">
                              <span>{job.views_count} views</span>
                              <span>{job.applications_count} applications</span>
                              <span>{job.conversion_rate}% conversion</span>
                            </div>
                          </div>
                          <Badge className={job.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {job.is_published ? 'Active' : 'Draft'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Candidate Demographics */}
                {candidateDemographics && (
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">Experience Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {candidateDemographics.experience_distribution.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="text-sm text-gray-600">{item.label}</span>
                            <Badge variant="secondary">{item.count}</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">Education Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {candidateDemographics.education_distribution.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="text-sm text-gray-600">{item.label}</span>
                            <Badge variant="secondary">{item.count}</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="text-sm font-medium">Location Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {candidateDemographics.location_distribution.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="text-sm text-gray-600">{item.label}</span>
                            <Badge variant="secondary">{item.count}</Badge>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-[#0F4C81] border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading analytics...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Subscription & Credits Tab */}
          <TabsContent value="subscription" className="mt-0">
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Current Plan */}
              <Card className="border-0 shadow-lg lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Current Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {subscription ? (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#0F4C81] to-emerald-600 rounded-xl text-white">
                        <div>
                          <p className="text-sm opacity-80">Your Plan</p>
                          <h3 className="text-2xl font-bold">{subscription.plan_name}</h3>
                          <p className="text-sm opacity-80 mt-1">
                            {subscription.status === 'active' ? 'Active' : subscription.status} • 
                            Expires {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                        <Button 
                          variant="secondary" 
                          onClick={() => setIsUpgradeDialogOpen(true)}
                          className="bg-white text-[#0F4C81] hover:bg-white/90"
                        >
                          Upgrade Plan
                        </Button>
                      </div>
                      
                      {/* Credits Usage */}
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="p-4 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <Briefcase className="h-4 w-4 text-blue-600" />
                            <span className="text-sm text-gray-600">Job Postings</span>
                          </div>
                          <p className="text-2xl font-bold text-slate-800">
                            {subscription.remaining_credits?.job_postings === -1 ? '∞' : subscription.remaining_credits?.job_postings || 0}
                          </p>
                          <p className="text-xs text-gray-500">
                            of {subscription.job_posting_credits === -1 ? 'Unlimited' : subscription.job_posting_credits} remaining
                          </p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="h-4 w-4 text-amber-600" />
                            <span className="text-sm text-gray-600">Featured Jobs</span>
                          </div>
                          <p className="text-2xl font-bold text-slate-800">
                            {subscription.remaining_credits?.featured_jobs === -1 ? '∞' : subscription.remaining_credits?.featured_jobs || 0}
                          </p>
                          <p className="text-xs text-gray-500">
                            of {subscription.featured_job_credits === -1 ? 'Unlimited' : subscription.featured_job_credits} remaining
                          </p>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="h-4 w-4 text-emerald-600" />
                            <span className="text-sm text-gray-600">Resume Access</span>
                          </div>
                          <p className="text-2xl font-bold text-slate-800">
                            {subscription.remaining_credits?.resume_accesses === -1 ? '∞' : subscription.remaining_credits?.resume_accesses || 0}
                          </p>
                          <p className="text-xs text-gray-500">
                            of {subscription.resume_access_credits === -1 ? 'Unlimited' : subscription.resume_access_credits} remaining
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="animate-spin h-8 w-8 border-4 border-[#0F4C81] border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading subscription...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Quick Actions */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start bg-[#0F4C81] hover:bg-[#0F4C81]/90"
                    onClick={() => setIsUpgradeDialogOpen(true)}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Upgrade Plan
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Download className="h-4 w-4 mr-2" />
                    Download Invoice
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={() => setIsSettingsDialogOpen(true)}>
                    <Calendar className="h-4 w-4 mr-2" />
                    Interview Settings
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Available Plans */}
            <h3 className="text-lg font-semibold mt-8 mb-4">Available Plans</h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {subscriptionPlans.map((plan) => (
                <Card 
                  key={plan.plan_id} 
                  className={`border-2 transition-all hover:shadow-lg ${
                    subscription?.plan_id === plan.plan_id 
                      ? 'border-[#0F4C81] bg-blue-50/50' 
                      : 'border-transparent'
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <h4 className="font-semibold text-lg">{plan.plan_name}</h4>
                      <div className="flex items-baseline gap-1 mt-2">
                        <span className="text-3xl font-bold text-[#0F4C81]">
                          {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString()}`}
                        </span>
                        {plan.price > 0 && <span className="text-gray-500">/month</span>}
                      </div>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {subscription?.plan_id === plan.plan_id ? (
                      <Badge className="w-full justify-center py-2 bg-[#0F4C81]">Current Plan</Badge>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => handleUpgradeSubscription(plan.plan_id)}
                      >
                        {plan.price === 0 ? 'Downgrade' : 'Upgrade'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Team Management Tab */}
          <TabsContent value="team" className="mt-0">
            <Card className="border-0 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#0F4C81]" />
                  Team Members
                </CardTitle>
                <Button onClick={() => setIsInviteTeamDialogOpen(true)} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {teamMembers.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0F4C81] to-emerald-600 flex items-center justify-center text-white font-semibold text-lg">
                          {member.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{member.name}</p>
                            {member.is_owner && (
                              <Badge variant="secondary" className="text-xs">Owner</Badge>
                            )}
                            {!member.is_active && !member.is_owner && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">Pending</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Copy Invite Link for pending members */}
                        {!member.is_active && !member.is_owner && member.invite_token && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              const link = `https://officiumconnect.in/team-invite/${member.invite_token}`;
                              navigator.clipboard.writeText(link);
                              toast.success('Invite link copied to clipboard!');
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Copy invite link"
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Link
                          </Button>
                        )}
                        {!member.is_owner && (
                          <Select 
                            value={member.role} 
                            onValueChange={(v) => handleUpdateTeamMemberRole(member.id, v)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {teamRoles.map((role) => (
                                <SelectItem key={role.id} value={role.id}>
                                  {role.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {member.is_owner ? (
                          <Badge className="bg-[#0F4C81]">Owner</Badge>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveTeamMember(member.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {teamMembers.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Team Members Yet</h3>
                      <p className="text-gray-500 mb-4">Invite your team to collaborate on hiring</p>
                      <Button onClick={() => setIsInviteTeamDialogOpen(true)} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                        <Plus className="h-4 w-4 mr-2" />
                        Invite First Member
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Role Permissions Info */}
                <div className="mt-8 p-4 bg-blue-50 rounded-xl">
                  <h4 className="font-medium text-[#0F4C81] mb-3">Team Roles & Permissions</h4>
                  <div className="grid gap-3 md:grid-cols-3">
                    {teamRoles.map((role) => (
                      <div key={role.id} className="bg-white p-3 rounded-lg">
                        <p className="font-medium text-sm">{role.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Job Create/Edit Dialog */}
      <Dialog open={isJobDialogOpen} onOpenChange={(open) => { if (!open) { setIsJobDialogOpen(false); setEditingJob(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Position Title *</Label>
                <Input 
                  value={jobForm.position_name}
                  onChange={(e) => setJobForm({...jobForm, position_name: e.target.value})}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              <div>
                <Label>Location *</Label>
                <Input 
                  value={jobForm.location}
                  onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                  placeholder="e.g., Bangalore, India"
                />
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Work Type</Label>
                <Select value={jobForm.work_type} onValueChange={(v) => setJobForm({...jobForm, work_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Office">Work from Office</SelectItem>
                    <SelectItem value="Work from Home">Remote</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employment Type</Label>
                <Select value={jobForm.employment_type} onValueChange={(v) => setJobForm({...jobForm, employment_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Experience Required</Label>
                <Input 
                  value={jobForm.experience_required}
                  onChange={(e) => setJobForm({...jobForm, experience_required: e.target.value})}
                  placeholder="e.g., 3-5 years"
                />
              </div>
            </div>
            
            {/* Salary */}
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Salary Range</Label>
                <Input 
                  value={jobForm.salary_range}
                  onChange={(e) => setJobForm({...jobForm, salary_range: e.target.value})}
                  placeholder="e.g., 12-18 LPA"
                />
              </div>
              <div>
                <Label>Education Required</Label>
                <Input 
                  value={jobForm.education_required}
                  onChange={(e) => setJobForm({...jobForm, education_required: e.target.value})}
                  placeholder="e.g., B.Tech/M.Tech"
                />
              </div>
              <div>
                <Label>Application Deadline</Label>
                <Input 
                  type="date"
                  value={jobForm.application_deadline}
                  onChange={(e) => setJobForm({...jobForm, application_deadline: e.target.value})}
                />
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Job Description *</Label>
              <Textarea 
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                placeholder="Describe the role, responsibilities, and ideal candidate..."
                rows={4}
              />
            </div>
            
            {/* Required Skills */}
            <div>
              <Label>Required Skills</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Add a skill"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('required_skills', newSkill, setNewSkill))}
                />
                <Button type="button" onClick={() => addListItem('required_skills', newSkill, setNewSkill)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.required_skills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    {skill}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeListItem('required_skills', idx)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Responsibilities */}
            <div>
              <Label>Key Responsibilities</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={newResponsibility}
                  onChange={(e) => setNewResponsibility(e.target.value)}
                  placeholder="Add a responsibility"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('responsibilities', newResponsibility, setNewResponsibility))}
                />
                <Button type="button" onClick={() => addListItem('responsibilities', newResponsibility, setNewResponsibility)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <ul className="space-y-1">
                {jobForm.responsibilities.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm bg-slate-50 p-2 rounded">
                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span className="flex-1">{item}</span>
                    <X className="h-4 w-4 text-gray-400 cursor-pointer hover:text-red-500" onClick={() => removeListItem('responsibilities', idx)} />
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Qualifications */}
            <div>
              <Label>Qualifications</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={newQualification}
                  onChange={(e) => setNewQualification(e.target.value)}
                  placeholder="Add a qualification"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('qualifications', newQualification, setNewQualification))}
                />
                <Button type="button" onClick={() => addListItem('qualifications', newQualification, setNewQualification)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <ul className="space-y-1">
                {jobForm.qualifications.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm bg-slate-50 p-2 rounded">
                    <Award className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="flex-1">{item}</span>
                    <X className="h-4 w-4 text-gray-400 cursor-pointer hover:text-red-500" onClick={() => removeListItem('qualifications', idx)} />
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Benefits */}
            <div>
              <Label>Benefits & Perks</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={newBenefit}
                  onChange={(e) => setNewBenefit(e.target.value)}
                  placeholder="Add a benefit"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addListItem('benefits', newBenefit, setNewBenefit))}
                />
                <Button type="button" onClick={() => addListItem('benefits', newBenefit, setNewBenefit)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.benefits.map((item, idx) => (
                  <Badge key={idx} className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                    {item}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeListItem('benefits', idx)} />
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Flags */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={jobForm.is_published}
                  onChange={(e) => setJobForm({...jobForm, is_published: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Publish immediately</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={jobForm.is_urgent}
                  onChange={(e) => setJobForm({...jobForm, is_urgent: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Mark as Urgent</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={jobForm.is_featured}
                  onChange={(e) => setJobForm({...jobForm, is_featured: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Feature this job</span>
              </label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setIsJobDialogOpen(false); setEditingJob(null); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateJob} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                <Save className="h-4 w-4 mr-2" />
                {editingJob ? 'Update Job' : 'Create Job'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Profile Dialog */}
      <Dialog open={isCompanyProfileDialogOpen} onOpenChange={setIsCompanyProfileDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Company Profile</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Logo & Banner Upload */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Company Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  {companyProfile.logo_url ? (
                    <img src={getImageUrl(companyProfile.logo_url, API)} alt="Logo" className="h-16 w-16 rounded-lg object-cover border" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Building2 className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleUploadLogo} />
                    <Button variant="outline" size="sm" asChild>
                      <span><Upload className="h-4 w-4 mr-2" />Upload Logo</span>
                    </Button>
                  </label>
                </div>
              </div>
              
              <div>
                <Label>Company Banner</Label>
                <div className="mt-2">
                  {companyProfile.banner_url ? (
                    <img 
                      src={getImageUrl(companyProfile.banner_url, API)} 
                      alt="Banner" 
                      className="h-20 w-full rounded-lg object-cover border" 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="h-20 w-full rounded-lg bg-slate-100 flex items-center justify-center">
                      <Image className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <label className="cursor-pointer mt-2 block">
                    <input type="file" accept="image/*" className="hidden" onChange={handleUploadBanner} />
                    <Button variant="outline" size="sm" asChild>
                      <span><Upload className="h-4 w-4 mr-2" />Upload Banner</span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Basic Info */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Industry</Label>
                <Input 
                  value={companyProfile.industry || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, industry: e.target.value})}
                  placeholder="e.g., Information Technology"
                />
              </div>
              <div>
                <Label>Company Size</Label>
                <Select value={companyProfile.company_size || ''} onValueChange={(v) => setCompanyProfile({...companyProfile, company_size: v})}>
                  <SelectTrigger><SelectValue placeholder="Select size" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 employees</SelectItem>
                    <SelectItem value="11-50">11-50 employees</SelectItem>
                    <SelectItem value="51-200">51-200 employees</SelectItem>
                    <SelectItem value="201-500">201-500 employees</SelectItem>
                    <SelectItem value="500+">500+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Founded Year</Label>
                <Input 
                  type="number"
                  value={companyProfile.founded_year || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, founded_year: parseInt(e.target.value) || ''})}
                  placeholder="e.g., 2015"
                />
              </div>
              <div>
                <Label>Headquarters</Label>
                <Input 
                  value={companyProfile.headquarters || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, headquarters: e.target.value})}
                  placeholder="e.g., Bangalore, India"
                />
              </div>
            </div>
            
            {/* About */}
            <div>
              <Label>About Company</Label>
              <Textarea 
                value={companyProfile.about || ''}
                onChange={(e) => setCompanyProfile({...companyProfile, about: e.target.value})}
                placeholder="Tell candidates about your company..."
                rows={4}
              />
            </div>
            
            {/* Culture */}
            <div>
              <Label>Company Culture</Label>
              <Textarea 
                value={companyProfile.culture || ''}
                onChange={(e) => setCompanyProfile({...companyProfile, culture: e.target.value})}
                placeholder="Describe your work culture..."
                rows={3}
              />
            </div>
            
            {/* Social Links */}
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label>Website</Label>
                <Input 
                  value={companyProfile.website || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, website: e.target.value})}
                  placeholder="https://yourcompany.com"
                />
              </div>
              <div>
                <Label>LinkedIn</Label>
                <Input 
                  value={companyProfile.linkedin || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, linkedin: e.target.value})}
                  placeholder="LinkedIn URL"
                />
              </div>
              <div>
                <Label>Twitter</Label>
                <Input 
                  value={companyProfile.twitter || ''}
                  onChange={(e) => setCompanyProfile({...companyProfile, twitter: e.target.value})}
                  placeholder="Twitter URL"
                />
              </div>
            </div>
            
            {/* Benefits */}
            <div>
              <Label>Benefits</Label>
              <div className="flex gap-2 mb-2">
                <Input 
                  value={newPerk}
                  onChange={(e) => setNewPerk(e.target.value)}
                  placeholder="Add a benefit (e.g., Health Insurance)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newPerk.trim()) {
                        setCompanyProfile(prev => ({
                          ...prev,
                          benefits: [...(prev.benefits || []), newPerk.trim()]
                        }));
                        setNewPerk('');
                      }
                    }
                  }}
                />
                <Button type="button" onClick={() => {
                  if (newPerk.trim()) {
                    setCompanyProfile(prev => ({
                      ...prev,
                      benefits: [...(prev.benefits || []), newPerk.trim()]
                    }));
                    setNewPerk('');
                  }
                }}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(companyProfile.benefits || []).map((benefit, idx) => (
                  <Badge key={idx} className="bg-emerald-100 text-emerald-700 flex items-center gap-1">
                    {benefit}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => {
                      setCompanyProfile(prev => ({
                        ...prev,
                        benefits: prev.benefits.filter((_, i) => i !== idx)
                      }));
                    }} />
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsCompanyProfileDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCompanyProfile} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                <Save className="h-4 w-4 mr-2" />
                Save Profile
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interview Scheduling Dialog */}
      <Dialog open={isInterviewDialogOpen} onOpenChange={(open) => { if (!open) { setIsInterviewDialogOpen(false); setSelectedApplication(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Interview</DialogTitle>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedApplication.full_name}</p>
                <p className="text-sm text-gray-500">for {selectedApplication.job_title}</p>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Date *</Label>
                  <Input 
                    type="date"
                    value={interviewForm.interview_date}
                    onChange={(e) => setInterviewForm({...interviewForm, interview_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input 
                    type="time"
                    value={interviewForm.interview_time}
                    onChange={(e) => setInterviewForm({...interviewForm, interview_time: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Interview Type</Label>
                  <Select value={interviewForm.interview_type} onValueChange={(v) => setInterviewForm({...interviewForm, interview_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Video Call">Video Call</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="In-person">In-person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration</Label>
                  <Select value={String(interviewForm.duration_minutes)} onValueChange={(v) => setInterviewForm({...interviewForm, duration_minutes: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {interviewForm.interview_type === 'Video Call' && (
                <div>
                  <Label>Meeting Link</Label>
                  <Input 
                    value={interviewForm.meeting_link}
                    onChange={(e) => setInterviewForm({...interviewForm, meeting_link: e.target.value})}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              )}
              
              {interviewForm.interview_type === 'In-person' && (
                <div>
                  <Label>Location</Label>
                  <Input 
                    value={interviewForm.location}
                    onChange={(e) => setInterviewForm({...interviewForm, location: e.target.value})}
                    placeholder="Office address"
                  />
                </div>
              )}
              
              <div>
                <Label>Notes for Candidate</Label>
                <Textarea 
                  value={interviewForm.notes}
                  onChange={(e) => setInterviewForm({...interviewForm, notes: e.target.value})}
                  placeholder="Any additional instructions..."
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setIsInterviewDialogOpen(false); setSelectedApplication(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleSendInterviewInvite} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                  <Send className="h-4 w-4 mr-2" />
                  Send Invite
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      <Dialog open={isApplicationDetailOpen} onOpenChange={(open) => { if (!open) { setIsApplicationDetailOpen(false); setSelectedApplication(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApplication && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#10B981] flex items-center justify-center text-white font-bold text-2xl">
                  {selectedApplication.full_name?.charAt(0) || 'C'}
                </div>
                <div>
                  <h3 className="font-semibold text-xl">{selectedApplication.full_name}</h3>
                  <p className="text-gray-500">{selectedApplication.email}</p>
                  {selectedApplication.phone && <p className="text-gray-500">{selectedApplication.phone}</p>}
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Applied For</p>
                  <p className="font-medium">{selectedApplication.job_title}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Status</p>
                  <Badge className={
                    selectedApplication.status === 'Applied' ? 'bg-blue-100 text-blue-700' :
                    selectedApplication.status === 'Shortlisted' ? 'bg-green-100 text-green-700' :
                    selectedApplication.status === 'Interview Scheduled' ? 'bg-purple-100 text-purple-700' :
                    selectedApplication.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }>{selectedApplication.status}</Badge>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-3">
                {selectedApplication.total_experience && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Experience</p>
                    <p className="font-medium">{selectedApplication.total_experience}</p>
                  </div>
                )}
                {selectedApplication.education && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Education</p>
                    <p className="font-medium">{selectedApplication.education}</p>
                  </div>
                )}
                {selectedApplication.current_city_state && (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="font-medium">{selectedApplication.current_city_state}</p>
                  </div>
                )}
              </div>
              
              {selectedApplication.skills && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedApplication.skills.split(',').map((skill, idx) => (
                      <Badge key={idx} variant="secondary">{skill.trim()}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedApplication.cover_letter && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Cover Letter</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedApplication.cover_letter}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'Rejected')}>
                  <XCircle className="h-4 w-4 mr-2 text-red-500" />
                  Reject
                </Button>
                <Button variant="outline" onClick={() => { setIsApplicationDetailOpen(false); setIsInterviewDialogOpen(true); }}>
                  <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                  Schedule Interview
                </Button>
                <Button onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'Shortlisted')} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Shortlist
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Invite Dialog */}
      <Dialog open={isInviteTeamDialogOpen} onOpenChange={setIsInviteTeamDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={teamInviteForm.name}
                onChange={(e) => setTeamInviteForm({...teamInviteForm, name: e.target.value})}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={teamInviteForm.email}
                onChange={(e) => setTeamInviteForm({...teamInviteForm, email: e.target.value})}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={teamInviteForm.role} onValueChange={(v) => setTeamInviteForm({...teamInviteForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                  <SelectItem value="hiring_manager">Hiring Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {teamInviteForm.role === 'recruiter' && 'Can view jobs and manage applications'}
                {teamInviteForm.role === 'hiring_manager' && 'Can manage jobs, applications, and view analytics'}
                {teamInviteForm.role === 'admin' && 'Full access except billing and ownership transfer'}
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsInviteTeamDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleInviteTeamMember} 
                className="bg-[#0F4C81] hover:bg-[#0F4C81]/90"
                disabled={!teamInviteForm.name || !teamInviteForm.email}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Upgrade Dialog */}
      <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upgrade Your Plan</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2 py-4">
            {subscriptionPlans.filter(p => p.plan_id !== 'free').map((plan) => (
              <Card 
                key={plan.plan_id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  subscription?.plan_id === plan.plan_id ? 'border-2 border-[#0F4C81]' : 'border'
                }`}
                onClick={() => handleUpgradeSubscription(plan.plan_id)}
              >
                <CardContent className="p-6">
                  <h4 className="font-semibold text-lg mb-2">{plan.plan_name}</h4>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-2xl font-bold text-[#0F4C81]">₹{plan.price.toLocaleString()}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {subscription?.plan_id === plan.plan_id && (
                    <Badge className="mt-4 w-full justify-center bg-[#0F4C81]">Current Plan</Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">
            Payment integration coming soon. For now, contact support to upgrade.
          </p>
        </DialogContent>
      </Dialog>

      {/* Interview Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Interview Scheduling Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Calendly URL</Label>
              <Input 
                value={interviewSettings.calendly_url || ''}
                onChange={(e) => setInterviewSettings({...interviewSettings, calendly_url: e.target.value})}
                placeholder="https://calendly.com/your-name/30min"
              />
              <p className="text-xs text-gray-500 mt-1">
                Add your Calendly link to allow candidates to self-schedule interviews
              </p>
            </div>
            
            <div>
              <Label>Default Meeting Duration</Label>
              <Select 
                value={String(interviewSettings.default_meeting_duration)} 
                onValueChange={(v) => setInterviewSettings({...interviewSettings, default_meeting_duration: parseInt(v)})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-4 grid-cols-2">
              <div>
                <Label>Working Hours Start</Label>
                <Input 
                  type="time"
                  value={interviewSettings.working_hours_start}
                  onChange={(e) => setInterviewSettings({...interviewSettings, working_hours_start: e.target.value})}
                />
              </div>
              <div>
                <Label>Working Hours End</Label>
                <Input 
                  type="time"
                  value={interviewSettings.working_hours_end}
                  onChange={(e) => setInterviewSettings({...interviewSettings, working_hours_end: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsSettingsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveInterviewSettings} className="bg-[#0F4C81] hover:bg-[#0F4C81]/90">
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployerDashboard;
